import dayjs from 'dayjs';
import {
    INFRASTRUCTURE_UNIT,
    STATES,
    WORKING_STATES,
    PRIMARY_STATES,
    DOWNLOAD_ACT_TYPES,
    DA_LINK_PROPERTIES,
    ENTITY,
    FREE_LICENSE_STATES,
    LEASING_CONTRACT
} from './deviceStateConstants';
import { LICENSES_TYPES_ID } from './constants';

const DATE_FORMAT = 'YYYY-MM-DD';

const mapOptionsToQueryParameter = (data, property) =>
    (Array.isArray(data) ? data : []).map(option => option[property]);
const mapOptionsMultidimensionalArrayToQueryParameter = data =>
    (Array.isArray(data) ? data.map(({ value }) => value) : []).flat();
const mapOptionsArrayToQueryParameter = data => mapOptionsToQueryParameter(data, 'value');
const mapOptionsArrayToQueryParameterById = data => mapOptionsToQueryParameter(data, 'id');
const mapOptionsArrayToQueryParameterArray = data => mapOptionsToQueryParameter(data, 'value');
const getOptionObjectToQueryParameter = data => data ? data.value : '';
const formatDate = value => (value?.format ? value.format(DATE_FORMAT) : value);
const clearOutEmptyValues = filters => {
    const clearedOutFilters = { ...filters };

    Object.keys(clearedOutFilters).forEach(key => {
        if (clearedOutFilters[key] == null || clearedOutFilters[key] === '' || clearedOutFilters[key].length === 0) {
            delete clearedOutFilters[key];
        }
    });

    return clearedOutFilters;
};

const getDeviceWorkingStateId = (deviceWorkingStates = {}) => {
    if (deviceWorkingStates.working) {
        return WORKING_STATES.WORKING.value;
    } else if (deviceWorkingStates.nonWorking) {
        return WORKING_STATES.NON_WORKING.value;
    } else if (deviceWorkingStates.workingConditionally) {
        return WORKING_STATES.WORKING_CONDITIONALLY.value;
    }
    return null;
};

const getPrimaryStateId = (primaryDeviceStates = {}) => {
    if (primaryDeviceStates.yes) {
        return PRIMARY_STATES.YES;
    } else if (primaryDeviceStates.no) {
        return PRIMARY_STATES.NO;
    }

    return null;
};

const getInfrastructureUnitId = infrastructureUnitStates => {
    if (infrastructureUnitStates?.yes) {
        return INFRASTRUCTURE_UNIT.YES;
    } else if (infrastructureUnitStates?.no) {
        return INFRASTRUCTURE_UNIT.NO;
    }

    return null;
};

const getLeasingContractId = leasingContractStates => {
    if (leasingContractStates?.yes) {
        return LEASING_CONTRACT.YES;
    } else if (leasingContractStates?.no) {
        return LEASING_CONTRACT.NO;
    }

    return null;
};

const getFreeLicenseStateId = (freeLicenseStates = {}) => {
    if (freeLicenseStates.yes) {
        return FREE_LICENSE_STATES.YES;
    } else if (freeLicenseStates.no) {
        return FREE_LICENSE_STATES.NO;
    }

    return null;
};

const getDeviceStateId = (deviceStates = {}) => {
    if (deviceStates.inUse) {
        return STATES.IN_USE;
    }
    if (deviceStates.onStock) {
        return STATES.ON_STOCK;
    }
    if (deviceStates.underRepair) {
        return STATES.UNDER_REPAIR;
    }
    if (deviceStates.ordered) {
        return STATES.ORDERED;
    }
    if (deviceStates.writeOff) {
        return STATES.WRITE_OFF;
    }
    if (deviceStates.inTransit) {
        return [STATES.IN_TRANSIT_TO_USER, STATES.IN_TRANSIT_TO_STOCK];
    }
    if (deviceStates.inOffice) {
        return STATES.IN_OFFICE;
    }

    return null;
};

export const getDeviceDownloadActLink = (entity, type, userId, language) => {
    let linkProp;
    let workspace;
    switch (entity) {
        case ENTITY.DEVICE:
            workspace = DA_LINK_PROPERTIES.DEVICE;
            break;
        case ENTITY.MOB_DEVICE:
            workspace = DA_LINK_PROPERTIES.MOB_DEVICE;
            break;
        default:
            return;
    }
    switch (type) {
        case DOWNLOAD_ACT_TYPES.RECEPTION:
            linkProp = workspace.RECEPTION;
            break;
        case DOWNLOAD_ACT_TYPES.RETURN:
            linkProp = workspace.RETURN;
            break;
        default:
            return;
    }
    return `${entity}/${linkProp}?userId=${userId}&language=${language}`;
};

export const mapDeviceFiltersStateToQuery = (state, googleFolderId) => {
    const filters = { ...state };

    filters.deviceTypeIds = mapOptionsArrayToQueryParameter(filters.deviceTypes);
    filters.deviceManufacturerIds = mapOptionsArrayToQueryParameter(filters.deviceManufacturers);
    filters.userIds = mapOptionsArrayToQueryParameter(filters.users);
    filters.officeIds = mapOptionsArrayToQueryParameter(filters.offices);
    filters.locationIds = mapOptionsArrayToQueryParameter(filters.whereabouts);
    filters.resourceOwnerIds = mapOptionsArrayToQueryParameter(filters.resourceOwner);
    filters.userPositionIds = mapOptionsArrayToQueryParameter(filters.positionsInCompany);
    filters.userProjectIds = mapOptionsArrayToQueryParameter(filters.projectsInCompany);
    filters.userPrimarySkillIds = mapOptionsArrayToQueryParameter(filters.primarySkill);
    filters.userRoleTypeIds = mapOptionsArrayToQueryParameter(filters.roleOnProject);
    filters.ids = mapOptionsArrayToQueryParameter(filters.ids);

    filters.operatingSystemNameIds = mapOptionsArrayToQueryParameterArray(filters.oSTypes);
    filters.processorNameIds = mapOptionsArrayToQueryParameterArray(filters.processorNameTypes);
    filters.processorCoreFrom = mapOptionsArrayToQueryParameterArray(filters.fromCoreCountTypes);
    filters.processorCoreTo = mapOptionsArrayToQueryParameterArray(filters.toCoreCountTypes);
    filters.diskSizeFrom = mapOptionsArrayToQueryParameterArray(filters.fromDiskSizeTypes);
    filters.diskSizeTo = mapOptionsArrayToQueryParameterArray(filters.toDiskSizeTypes);
    filters.ramFrom = mapOptionsArrayToQueryParameterArray(filters.fromRamSizeTypes);
    filters.ramTo = mapOptionsArrayToQueryParameterArray(filters.toRamSizeTypes);
    filters.ramSlotFrom = mapOptionsArrayToQueryParameterArray(filters.fromRamSlotTypes);
    filters.ramSlotTo = mapOptionsArrayToQueryParameterArray(filters.toRamSlotTypes);

    filters.assignmentDateFrom = formatDate(filters.assignmentDateFrom);
    filters.assignmentDateTo = formatDate(filters.assignmentDateTo);
    filters.deliveryDateFrom = formatDate(filters.deliveryDateFrom);
    filters.deliveryDateTo = formatDate(filters.deliveryDateTo);
    filters.LeasingContractEndDateFrom = formatDate(filters.LeasingContractEndDateFrom);
    filters.LeasingContractEndDateTo = formatDate(filters.LeasingContractEndDateTo);
    filters.LeasingContractIds = mapOptionsArrayToQueryParameter(filters.LeasingContractIds);

    const workingStateId = getDeviceWorkingStateId(filters.condition);
    if (workingStateId) filters.workingStateId = workingStateId;

    filters.isPrimary = getPrimaryStateId(filters.primary);

    const isInfrastructureUnit = getInfrastructureUnitId(filters.infrastructureUnit);

    if (isInfrastructureUnit !== null) {
        filters.isInfrastructureUnit = isInfrastructureUnit;
    }

    const isLeasingContract = getLeasingContractId(filters.leasingContract);

    if (isLeasingContract !== null) {
        filters.isLeasingContract = isLeasingContract;
    }

    filters.stateIds = getDeviceStateId(filters.state);

    if (googleFolderId) {
        filters.folderId = googleFolderId;
        delete filters.itemsPerPage;
    }

    delete filters.deviceTypes;
    delete filters.users;
    delete filters.offices;
    delete filters.whereabouts;
    delete filters.resourceOwner;
    delete filters.deviceTypeName;
    delete filters.condition;
    delete filters.state;
    delete filters.primary;
    delete filters.infrastructureUnit;
    delete filters.leasingContract;
    delete filters.positionsInCompany;
    delete filters.projectsInCompany;
    delete filters.primarySkill;
    delete filters.roleOnProject;
    delete filters.oSTypes;
    delete filters.processorNameTypes;
    delete filters.fromCoreCountTypes;
    delete filters.toCoreCountTypes;
    delete filters.fromDiskSizeTypes;
    delete filters.toDiskSizeTypes;
    delete filters.fromRamSizeTypes;
    delete filters.toRamSizeTypes;
    delete filters.fromRamSlotTypes;
    delete filters.toRamSlotTypes;

    return clearOutEmptyValues(filters);
};

export const mapMobileDeviceFiltersStateToQuery = (state, googleFolderId) => {
    const deviceFiltersQuery = mapDeviceFiltersStateToQuery(state);
    const mobileDeviceFiltersQuery = { ...deviceFiltersQuery };

    mobileDeviceFiltersQuery.stateId = deviceFiltersQuery.stateIds;

    mobileDeviceFiltersQuery.userProjectIds = mapOptionsArrayToQueryParameter(mobileDeviceFiltersQuery.projects);
    mobileDeviceFiltersQuery.osVersionIds = mapOptionsArrayToQueryParameter(mobileDeviceFiltersQuery.osVersions);
    mobileDeviceFiltersQuery.screenIds = mapOptionsArrayToQueryParameter(mobileDeviceFiltersQuery.screens);

    if (googleFolderId) {
        mobileDeviceFiltersQuery.folderId = googleFolderId;
        delete mobileDeviceFiltersQuery.itemsPerPage;
    }

    delete mobileDeviceFiltersQuery.stateIds;
    delete mobileDeviceFiltersQuery.projects;
    delete mobileDeviceFiltersQuery.osVersions;
    delete mobileDeviceFiltersQuery.screens;
    delete mobileDeviceFiltersQuery.rangeOSVersion;

    return clearOutEmptyValues(mobileDeviceFiltersQuery);
};

export const mapLicenseFiltersStateToQuery = (state, googleFolderId) => {
    const filters = { ...state };

    filters.deliveryDateFrom = formatDate(filters.deliveryDateFrom);
    filters.deliveryDateTo = formatDate(filters.deliveryDateTo);

    filters.expirationDateFrom = formatDate(filters.expirationDateFrom);
    filters.expirationDateTo = formatDate(filters.expirationDateTo);

    const isLicenseTypeUser = filters.licenseTypes?.some(el => el.id === LICENSES_TYPES_ID.user);
    const isLicenseTypeVM = filters.licenseTypes?.some(el => el.id === LICENSES_TYPES_ID.vm);
    const isLicenseTypeIU = filters.licenseTypes?.some(el => el.id === LICENSES_TYPES_ID.infrastructureUnit);

    if (isLicenseTypeUser) {
        filters.userIds = mapOptionsArrayToQueryParameter(filters.users);
    }

    if (isLicenseTypeVM) {
        filters.virtualMachineIds = mapOptionsArrayToQueryParameter(filters.devices);
    }

    if (isLicenseTypeIU) {
        filters.deviceIds = mapOptionsArrayToQueryParameter(filters.infrastructureUnit);
    }

    filters.licenseManufacturerIds = mapOptionsArrayToQueryParameter(filters.licenseManufacturers);
    filters.productTitleLicenseIds = mapOptionsMultidimensionalArrayToQueryParameter(filters.licenseTitles);
    filters.officeIds = mapOptionsArrayToQueryParameter(filters.offices);
    filters.resourceOwnerIds = mapOptionsArrayToQueryParameter(filters.resourceOwner);
    filters.licenseNameParts = mapOptionsArrayToQueryParameter(filters.licenseNameParts);
    filters.licenseIds = mapOptionsArrayToQueryParameter(filters.licenseIds);
    filters.licenseVerifyKind = getOptionObjectToQueryParameter(filters.licenseVerifyKind);
    filters.isFreeLicense = getFreeLicenseStateId(filters.freeLicense);
    filters.deviceIds = mapOptionsArrayToQueryParameter(filters.infrastructureUnit);
    filters.projectIds = mapOptionsArrayToQueryParameter(filters.projects);
    filters.licenseTypeIds = mapOptionsArrayToQueryParameterById(filters.licenseTypes);

    if (googleFolderId) {
        filters.folderId = googleFolderId;
        delete filters.itemsPerPage;
    }

    delete filters.offices;
    delete filters.licenseTypes;
    delete filters.devices;
    delete filters.users;
    delete filters.licenseManufacturers;
    delete filters.freeLicense;
    delete filters.licenseTitles;
    delete filters.locations;
    delete filters.resourceOwner;
    delete filters.infrastructureUnit;
    delete filters.projects;

    return clearOutEmptyValues(filters);
};

export const mapReviewLicenseFiltersStateToQuery = state => {
    const filters = { ...state };

    filters.userIds = mapOptionsArrayToQueryParameter(filters.users);
    filters.deviceIds = mapOptionsArrayToQueryParameter(filters.infrastructureUnits);
    filters.virtualMachineIds = mapOptionsArrayToQueryParameter(filters.virtualMachines);

    delete filters.users;
    delete filters.infrastructureUnits;
    delete filters.virtualMachines;

    return clearOutEmptyValues(filters);
};

export const mapVMFiltersStateToQuery = (state, googleFolderId) => {
    const filters = { ...state };

    const vmTitleIds = mapOptionsArrayToQueryParameterArray(filters.virtualMachines);
    const vmIds = mapOptionsArrayToQueryParameterArray(filters.virtualMachineIds);
    filters.virtualMachineIds = vmTitleIds.concat(vmIds);

    if (googleFolderId) {
        filters.folderId = googleFolderId;
        delete filters.itemsPerPage;
    }

    delete filters.virtualMachines;

    return clearOutEmptyValues(filters);
};

export const mapUWCFiltersStateToQuery = state => {
    const filters = { ...state };

    filters.officeIds = mapOptionsArrayToQueryParameter(filters.officeIds);

    return clearOutEmptyValues(filters);
};

export const mapEmployeeAssetsFiltersStateToQuery = (state, googleFolderId) => {
    const filters = { ...state };

    filters.userId = filters.currentUser.userId;

    if (googleFolderId) {
        filters.folderId = googleFolderId;
    }

    return clearOutEmptyValues(filters);
};

export const mapLicenseReviewFiltersStateToQuery = (state, googleFolderId) => {
    const filters = { ...state };

    if (googleFolderId) {
        filters.folderId = googleFolderId;
    }

    return clearOutEmptyValues(filters);
};

export const mapFlatDataToQueryMap = data =>
    Object.keys(data)
        .map(key => {
            if (Array.isArray(data[key])) return data[key].map(value => `${key}=${value}`).join('&');
            return `${key}=${data[key]}`;
        })
        .join('&');

export const mapDeviceTypesToTypeaheadSuggestions = data => {
    const groupMap = data.reduce((reducedGroupMap, deviceType) => {
        let group = reducedGroupMap[deviceType.deviceTypeCategory?.id];

        if (!group) {
            group = reducedGroupMap[deviceType.deviceTypeCategory?.id] = {
                value: deviceType.deviceTypeCategory?.id,
                label: deviceType.deviceTypeCategory?.name,
                options: []
            };
        }

        group.options.push({
            value: deviceType.id,
            label: deviceType.name,
            deviceTypeCategory: deviceType.deviceTypeCategory
        });

        return reducedGroupMap;
    }, {});

    const groups = Object.values(groupMap);

    // Sort groupped options
    groups.forEach(group => {
        group.options.sort((a, b) => {
            if (a.label < b.label) return -1;
            if (a.label > b.label) return 1;
            return 0;
        });

        // First option in sectionped options is section itself
        group.options.unshift({
            value: group.value,
            label: group.label,
            isDeviceTypeCategory: true,
            categoryDeviceTypes: [...group.options]
        });
    });

    return groups;
};

export const mapFiltersToSearchString = filters => {
    const clearedOutFilters = clearOutEmptyValues(filters);
    const formattedFilters = Object.entries(clearedOutFilters).reduce(
        (res, [key, value]) => ({
            ...res,
            [key]: typeof value === 'object' ? JSON.stringify(value) : value
        }),
        {}
    );

    const searchParams = new URLSearchParams(formattedFilters);

    return searchParams.toString();
};

export const mapSearchStringToFilters = searchString => {

    const searchParams = new URLSearchParams(searchString);

    const filters = {};

    searchParams.forEach((value, key) => {
        try {
            filters[key] = JSON.parse(value);
            switch (key.toLowerCase().includes('date')) {
                case true:
                    filters[key] = dayjs(filters[key]);
                    break;
                default:
                    break;
            }
        } catch (_err) {
            filters[key] = value;
        }
    });

    return filters;
};
